/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, OnDestroy, Optional, NgZone } from '@angular/core';
import { Observable, of, Subject, merge, fromEvent, combineLatest } from 'rxjs';
import { map, takeUntil, shareReplay, distinctUntilChanged, tap, startWith, scan, filter } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { AccessibilityTypes } from 'bridge-commands/types/common/accessibility.types';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { NvInputModality } from 'input-modality/lib/input-modality.type';
import { BridgeService } from '../bridge/bridge.service';
import { LoggingService, Logger } from '../logging/logging.service';
import { ACCESSIBILITY_CONFIG, AccessibilityConfig } from './accessibility.config';

/**
 * Service for accessibility features. In general, app behavior should not be altered based on whether
 * accessibility features are enabled.
 */
@Injectable({
    providedIn: 'root'
})
export class AccessibilityService implements OnDestroy {
    private readonly logger: Logger;
    private readonly destroy$ = new Subject<void>();
    private accessibilityInfo$?: Observable<Types.Common.Accessibility.AccessibilityInfo>;
    private enabled = false;
    private documentFocus$?: Observable<boolean>;
    private screenReaderNavigating$?: Observable<boolean>;

    constructor(
        private bridgeService: BridgeService,
        private inputModalityService: InputModalityService,
        private ngZone: NgZone,
        loggingService: LoggingService,
        @Optional() @Inject(ACCESSIBILITY_CONFIG) config: AccessibilityConfig
    ) {
        this.logger = loggingService.getLogger('AccessibilityService');
        this.enabled = !!config?.enable;
    }

    getAccessibilityInfo(): Observable<Types.Common.Accessibility.AccessibilityInfo> {
        if (!this.accessibilityInfo$) {
            if (this.enabled) {
                this.logger.info('Registering for accessibility callbacks');

                this.accessibilityInfo$ = this.bridgeService
                    .executeCommand(Commands.Common.Accessibility.RegisterAccessibilityCallbacks)
                    .pipe(
                        takeUntil(this.destroy$),
                        distinctUntilChanged((prev, curr) => {
                            const areEqual =
                                JSON.stringify(prev?.accessibilityFeaturesEnabled) ===
                                JSON.stringify(curr?.accessibilityFeaturesEnabled);
                            return areEqual;
                        }),
                        shareReplay(1)
                    );
            } else {
                this.accessibilityInfo$ = of({ accessibilityFeaturesEnabled: [] });
            }
        }

        return this.accessibilityInfo$;
    }

    onAccessibilityStateChanged(): Observable<boolean> {
        return this.getAccessibilityInfo().pipe(
            map(response => {
                const isEnabled = (response?.accessibilityFeaturesEnabled?.length ?? 0) > 0;
                return isEnabled;
            })
        );
    }

    onScreenReaderStateChanged(): Observable<boolean> {
        return this.getAccessibilityInfo().pipe(
            map(response => {
                const isScreenReaderEnabled = response?.accessibilityFeaturesEnabled?.includes(
                    AccessibilityTypes.AccessibilityFeatureEnum.ScreenReader
                );
                return isScreenReaderEnabled;
            })
        );
    }

    /**
     * Returns an observable that uses a heuristic to determine when the user is actively navigating with a screen reader.
     *
     * Use this to make dynamic adjustments to components, DOM elements, event listeners, or app behavior
     * while the screen reader is in active use. For example: hiding spatial navigation containers,
     * modifying tabindex values, or changing interaction patterns.
     *
     * **Note**: This is a heuristic based on indicators of screen reader activity, not a definitive signal.
     * There is no direct browser API to detect active screen reader navigation, so this combines multiple
     * data sources to make an educated determination.
     *
     * Use `onScreenReaderStateChanged()` to detect when the screen reader is enabled/disabled.
     * Use this method to detect when the screen reader is actually being used for navigation.
     *
     * **Important**: This observable always emits outside of Angular's zone for performance optimization.
     * All subscribers MUST wrap their callbacks in `ngZone.run()` to ensure proper change detection.
     *
     * @returns Observable<boolean> - true when the user is believed to be navigating with the screen reader
     */
    onScreenReaderNavigating(): Observable<boolean> {
        if (!this.screenReaderNavigating$) {
            this.screenReaderNavigating$ = this.ngZone.runOutsideAngular(() => {
                const screenReader$ = this.getScreenReaderStream();
                const inputModality$ = this.getInputModalityStream();
                const documentFocus$ = this.getDocumentFocusStream();

                return combineLatest([documentFocus$, screenReader$, inputModality$]).pipe(
                    scan((currentState, [documentHasFocus, screenReaderEnabled, inputModality]) => {
                        return this.resolveScreenReaderNavigationState(
                            currentState,
                            documentHasFocus,
                            screenReaderEnabled,
                            inputModality
                        );
                    }, false),
                    startWith(false),
                    distinctUntilChanged(),
                    shareReplay(1)
                );
            });
        }
        return this.screenReaderNavigating$;
    }

    private getDocumentFocusStream(): Observable<boolean> {
        if (!this.documentFocus$) {
            this.documentFocus$ = merge(
                fromEvent<FocusEvent>(document, 'focusin', { passive: true, capture: true }),
                fromEvent<FocusEvent>(document, 'focusout', { passive: true, capture: true })
            ).pipe(
                filter(event => event.relatedTarget === null),
                map(_event => this.hasDocumentFocus()),
                startWith(this.hasDocumentFocus()),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            );
        }
        return this.documentFocus$;
    }

    private getScreenReaderStream(): Observable<boolean> {
        return this.onScreenReaderStateChanged().pipe(distinctUntilChanged(), takeUntil(this.destroy$));
    }

    private getInputModalityStream(): Observable<NvInputModality | null> {
        return this.inputModalityService.inputModalityChanged$.pipe(
            startWith(null as NvInputModality | null), // Default to null
            shareReplay(1),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Determines if the document currently has focus for accessibility purposes.
     * Returns true when the document has focus and there's an active element that's not just the body.
     *
     * @returns true if document has meaningful focus, false otherwise
     */
    private hasDocumentFocus(): boolean {
        const documentHasFocus = document.hasFocus();
        const activeElement = document.activeElement;
        const activeElementHasFocus = activeElement?.matches(':focus') || false;
        const isBodyActive = activeElement === document.body;

        return documentHasFocus && (activeElementHasFocus || !isBodyActive);
    }

    /**
     * Resolves the screen reader navigation state using a heuristic based on current conditions.
     * Uses entry and exit conditions to manage state transitions.
     */
    private resolveScreenReaderNavigationState(
        currentState: boolean,
        documentHasFocus: boolean,
        screenReaderEnabled: boolean,
        inputModality: NvInputModality | null
    ): boolean {
        if (currentState) {
            // Currently navigating - check exit conditions
            const screenReaderDisabled = !screenReaderEnabled;
            const nonMouseInput = inputModality !== null && inputModality !== NvInputModality.Mouse;
            const documentRegainedFocus = documentHasFocus;

            return !(screenReaderDisabled || nonMouseInput || documentRegainedFocus);
        } else {
            // Currently not navigating - check entry conditions
            const screenReaderIsEnabled = screenReaderEnabled;
            const documentLostFocus = !documentHasFocus;

            return screenReaderIsEnabled && documentLostFocus;
        }
    }

    ngOnDestroy(): void {
        this.logger.info('AccessibilityService destroyed');
        this.destroy$.next();
        this.destroy$.complete();
    }
}
